import {
	useGetInvoiceLazyQuery,
	useGetContactLazyQuery,
	useGetProjectLazyQuery,
} from "@repo/server-api";

import {
	ResultFromData,
	ResultFromException,
	useNotificationGraphql,
} from "../../../shared/src/common/NotificationGraphlqlHook";

import type { GraphqlQueryResult } from "../../../shared/src/common/NotificationGraphlqlHook";
import type { Invoice } from "../types/CommercialDocumentTypes";
import type { Invoice as ServerInvoice, Contact, Project } from "@repo/server-api";
import { IdWithName } from "@repo/shared/common";
import { normalizeContact, normalizeProject } from "@repo/legacy/components/helper/Index";

type IdWithNameWithEmail = IdWithName & { email: string };

export function useInvoiceLoader({ onFetched }: { onFetched: (invoice: Invoice) => void }) {
	const [fetchInvoice] = useGetInvoiceLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [fetchContact] = useGetContactLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [fetchProject] = useGetProjectLazyQuery({
		fetchPolicy: "no-cache",
	});

	const contactGetter = async (id: string): Promise<IdWithName | IdWithNameWithEmail | null> => {
		let contact: IdWithName | null = null;

		contact = await fetchContact({ variables: { id } })
			.then((result) => {
				if (result.data?.node && (result.data.node as Contact).__typename === "Contact") {
					return normalizeContact(result.data.node as Contact);
				}
				return null;
			})
			.catch(() => {
				return null;
			});

		return contact;
	};

	const projectGetter = async (id: string): Promise<IdWithName | null> => {
		let project: IdWithName | null = null;

		project = await fetchProject({ variables: { id } })
			.then((result) => {
				if (
					result.data?.node &&
					(result.data.node as unknown as Project).__typename === "Project"
				) {
					return normalizeProject(result.data.node as unknown as Project);
				}
				return null;
			})
			.catch(() => {
				return null;
			});

		return project;
	};

	const mapInvoice = async (node: ServerInvoice): Promise<Invoice> => {
		const client = node.clientId ? await contactGetter(node.clientId) : null;
		const contractor = node.contractorId ? await contactGetter(node.contractorId) : null;
		const contactPerson = node.clientContactPersonId
			? await contactGetter(node.clientContactPersonId)
			: null;
		const project = node.projectId ? await projectGetter(node.projectId) : null;

		return {
			id: node.id,
			number: node.number ?? "",
			project: project,
			leftTotal: node.grossTotal - node.paidTotal,
			grossTotal: node.grossTotal,
			issueDate: node.issueDate ?? "",
			dueDate: node.dueDate ?? "",
			client: client,
			type: node.documentType,
			status: node.status,
			contactPerson: contactPerson as IdWithNameWithEmail | null,
			contractor: contractor,
			paymentDeadlineDays: node.paymentDeadlineInDays,
			headerNote: node.introductoryText ?? null,
			footerNote: node.concludingText ?? null,
			items: node.items?.map((item) => ({
				id: item.id,
				name: item.name,
				description: item.description,
				discount: item.discountPercent ?? 0,
				quantity: item.quantity,
				unit: item.unit,
				unitPrice: item.unitPriceNet,
				totalPrice: item.lineNetTotal,
				tax: item.tax,
				taxCode: item.taxCode,
			})),
			notes: node.notes ?? null,
			netTotal: node.netTotal ?? null,
			isProforma: node.isProforma,
		};
	};

	const fetchServerData = async (id: string): Promise<GraphqlQueryResult<Invoice>> => {
		const result = await fetchInvoice({ variables: { id } });

		if (result.error) return ResultFromException<Invoice>(result.errors);

		const node = result.data?.node as unknown as ServerInvoice;

		if (node?.__typename !== "Invoice")
			return ResultFromException<Invoice>([
				{
					message: "Invoice not found",
				},
			]);

		return ResultFromData<Invoice>(await mapInvoice(node));
	};

	const { save: fetchData, loading } = useNotificationGraphql<string, Invoice>({
		onComplete: (invoice: Invoice) => {
			onFetched(invoice);
		},

		graphqlCall: fetchServerData,
	});

	return { fetchData, loading };
}
